import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import CodeQuotesModal from '@components/interview/Analysis/CodeQuotesModal';
import Spinner from '@components/spinner/Spinner';
import {
  useFetchLensSummaries,
  useFetchStudyTags,
  useFetchTranscriptDetail,
  useFetchTranscriptSummary,
} from '@hooks/getQueries';
import {
  useAddStudyTag,
  useQueueSemanticEmbedding,
  useUpdateTranscriptName,
} from '@hooks/postQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import { Icon } from '@iconify/react';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tab,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import axiosServices from '@utils/axios';
import { formatInTitleCase, formatMsToMin, mediaType } from '@utils/constants';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import EditInterviewer from './EditInterviewer';

// Import Lens Summary Components
import { UserMic } from '@components/icon/svg';
import {
  useFetchTeamMembers,
  useSingleInterview,
  useSingleInterviewParticipant,
} from '@hooks/getQueries';
import { editTranscriptText } from '@services/interview';
import { useQueryClient } from '@tanstack/react-query';
import { useUserPermission } from '../../../hooks/userPermission';
import AssignedTagInTranscript from '../Tag/AssignedTagInTranscript';
import AssignTags from '../Tag/AssignTags';
import ParticipantDetailsCollapsible from './ParticipantsDetailsCollapsible';
import { RecordingFile } from './RecordingFile';
import CustomAnalysisTranscript from './transcript-lens-summaries/CustomAnalysisTranscript';
import EquityAuditAnalysisTranscript from './transcript-lens-summaries/EquityAuditAnalysisTranscript';
import FramingDiscourseAnalysisTranscript from './transcript-lens-summaries/FramingDiscourseAnalysisTranscript';
import JourneyPainAnalysisTranscript from './transcript-lens-summaries/JourneyPainAnalysisTranscript';
import JtbdMapAnalysisTranscript from './transcript-lens-summaries/JtbdMapAnalysisTranscript';
import NarrativeArcAnalysisTranscript from './transcript-lens-summaries/NarrativeArcAnalysisTranscript';
import OutcomeBarrierAnalysisTranscript from './transcript-lens-summaries/OutcomeBarrierAnalysisTranscript';
import PersonaTaskAnalysisTranscript from './transcript-lens-summaries/PersonaTaskAnalysisTranscript';
import PhenomenologicalAnalysisTranscript from './transcript-lens-summaries/PhenomenologicalAnalysisTranscript';
import ReflexivityTrackerAnalysisTranscript from './transcript-lens-summaries/ReflexivityTrackerAnalysisTranscript';
import ResonanceScanAnalysisTranscript from './transcript-lens-summaries/ResonanceScanAnalysisTranscript';
import SaturationGaugeAnalysisTranscript from './transcript-lens-summaries/SaturationGaugeAnalysisTranscript';
import SentimentSpectrumAnalysisTranscript from './transcript-lens-summaries/SentimentSpectrumAnalysisTranscript';
import TocAlignmentAnalysisTranscript from './transcript-lens-summaries/TocAlignmentAnalysisTranscript';
// const hashStringToColor = (str) => {
//   let hash = 0;
//   for (let i = 0; i < str.length; i++) {
//     hash = str.charCodeAt(i) + ((hash << 5) - hash);
//   }
//   const h = hash % 360;
//   return `hsl(${h}, 70%, 85%)`;
// };

// Helper function to highlight search term in text
const highlightSearchTerm = (text, searchTerm, turnId, highlightedTurnId) => {
  if (!text || !searchTerm || highlightedTurnId !== turnId) {
    return text;
  }

  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === searchTerm.toLowerCase()) {
      return (
        <span
          key={index}
          style={{
            backgroundColor: '#ffeb3b',
            padding: '2px 4px',
            borderRadius: '3px',
            fontWeight: 'bold',
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

// Helper function to format lens type names
const formatLensType = (type) => {
  if (!type) return 'Summary'; // Default name if type is missing
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const TranscriptDetail = ({ transcripts }) => {
  const editTranscriptNameRef = useRef(null);
  const textareaRef = useRef(null);
  const transcriptBlcokRef = useRef(null);
  const playerRef = useRef(null);
  const autoScrollWhilePlayingRef = useRef({});
  // const [hoveredCode] = useState(null);
  const [openCodeModal, setOpenCodeModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const { transcriptId, id } = useParams();
  const [value, setValue] = useState('1');
  const [openPopoverTranscriptName, setOpenPopoverTranscriptName] = useState(null);
  const [openPopoverInterview, setOpenPopoverInterview] = useState(null);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [tempTranscripts, setTempTranscripts] = useState([]);
  const [openTagModal, setOpenTagModal] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const searchTermFromUrl = searchParams.get('term');
  const scrollToTurnId = searchParams.get('scroll_to');

  // Add state for searchTerm
  const [searchTerm, setSearchTerm] = useState(searchTermFromUrl || '');

  // const [codesQuotesDict, setCodesQuotesDict] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const [editTranscriptName, setEditTranscriptName] = useState('');
  const { mutate: updateTranscriptName, isPending: isUpdatingTranscriptName } =
    useUpdateTranscriptName();
  const { mutateAsync: addStudyTag, isPending: isAddingStudyTag } = useAddStudyTag();
  const { mutate: queueSemanticEmbedding, isPending: isQueueingSemanticEmbedding } =
    useQueueSemanticEmbedding();
  // const { getColorByCodeName } = useGetCodeColorByCategory(id);

  const { open, handleConfirm, handleClose } = useConfirmationDialog();

  const [hoveredBoxId, setHoveredBoxId] = useState(null);
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [editedTranscriptText, setEditedTranscriptText] = useState('');
  // // video playback
  // const { data: mediaData, isLoading: isLoadingMedia } = useStudyRecording(transcriptId);
  // console.log(mediaData);
  const [playing, setPlaying] = useState(false);
  const [playingTurnItem, setPlayingTurnItem] = useState(null);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  // turn highlighting from search
  const [highlightedTurnId, setHighlightedTurnId] = useState(null);
  // participant details panel expanded state
  const [participantDetailsExpanded, setParticipantDetailsExpanded] = useState(true);
  const { data: tagsList, isPending: isFetchingTags } = useFetchStudyTags({
    studyId: id,
    searchQuery: tagSearchQuery,
  });

  const handleSeek = (item) => {
    let seekToTime = item.start_time / 1000;
    if (transcriptDetail?.media_type === mediaType.audio) {
      const audio = playerRef.current;
      playerRef.current.currentTime = seekToTime;
      audio.play();
    } else {
      playerRef.current?.seekTo(seekToTime, 'seconds');
    }
  };

  useEffect(() => {
    const playedMs = playedSeconds * 1000;

    const activeTurnItem = tempTranscripts.find(
      (item) => playedMs >= item.start_time && playedMs <= item.end_time,
    );
    setPlayingTurnItem(activeTurnItem || null);
  }, [playedSeconds]);

  useEffect(() => {
    if (!playingTurnItem) return;

    const node = autoScrollWhilePlayingRef.current[playingTurnItem.id];
    if (node) {
      node.scrollIntoView({
        behavior: 'smooth', // animate scroll
        block: 'center', // try to center it in the viewport
      });
    }
  }, [playingTurnItem]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleOpenPopoverTranscriptName = (e) => {
    setOpenPopoverTranscriptName(e.currentTarget);
    setTimeout(() => {
      editTranscriptNameRef?.current?.focus();
    }, 1);
  };

  useEffect(() => {
    if (editingItem !== null && textareaRef.current) {
      const textarea = textareaRef.current;

      if (textarea.value.endsWith('\n')) {
        textarea.value = textarea.value.replace(/\n+$/, '');
        setEditedTranscriptText(textarea.value);
      }
      textarea.focus();
      setTimeout(() => {
        const len = textarea.value.length;
        textarea.setSelectionRange(len, len);
      }, 0);
    }
  }, [editingItem]);
  const handleOpenInterviewPopover = (e) => {
    setOpenPopoverInterview(e.currentTarget);
  };
  const handleCloseTagModal = () => {
    setOpenTagModal(false);
  };
  const { data: transcriptDetail, isPending } = useFetchTranscriptDetail({
    transcriptId,
    studyId: id,
  });

  // Lazy load summary only when Summary tab is active
  const { data: transcriptSummary, isLoading: isLoadingSummary } = useFetchTranscriptSummary({
    transcriptId,
    enabled: value === '2', // Only fetch when Summary tab is clicked
  });

  const { data: lensSummaries, isLoading: isLoadingLensSummaries } = useFetchLensSummaries({
    transcriptId,
  });

  const { data: interview } = useSingleInterview(id);
  const { data: teamMembers } = useFetchTeamMembers({
    studyId: id,
    studyUrlType: 'qualitative-studies',
  });
  const { data: participant } = useSingleInterviewParticipant(transcriptId, id);
  const { canEdit } = useUserPermission({
    entity: interview,
    teamMembers: teamMembers,
  });
  const fetchCodesQuotes = async () => {
    try {
      const storedId = id;

      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const url = `${backendUrl}/qualitative-studies/${storedId}/codes_quotes/`;
      const response = await axiosServices.get(url);

      let result = response.data;
      if (typeof result === 'string') {
        result = JSON.parse(result);
      }

      if (result?.codes_quotes_dict) {
        // Cache the result
        const updatedCache = {
          [storedId]: {
            data: result,
            timestamp: Date.now(),
          },
        };
        localStorage.setItem('codes_quotes_dict', JSON.stringify(updatedCache));
        // setCodesQuotesDict(result.codes_quotes_dict);
      } else {
        // setCodesQuotesDict(null);
      }
    } catch (error) {
      console.error('Error fetching codes quotes:', error);
    }
  };

  const transcriptName = transcriptDetail?.name || transcriptDetail?.id;

  // Update search term when URL parameter changes
  useEffect(() => {
    setSearchTerm(searchTermFromUrl || '');
  }, [searchTermFromUrl]);

  useEffect(() => {
    if (transcriptId) {
      fetchCodesQuotes();
      setPlaying(false);
      setPlayingTurnItem(null);
      setPlayedSeconds(0);

      // Handle turn highlighting from search navigation
      if (location.state?.highlightTurnId) {
        setHighlightedTurnId(location.state.highlightTurnId);

        // Scroll to the highlighted turn after a brief delay to ensure rendering is complete
        setTimeout(() => {
          const turnElement = autoScrollWhilePlayingRef.current[location.state.highlightTurnId];
          if (turnElement) {
            turnElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 500);

        // Clear the highlighting after a few seconds
        // setTimeout(() => {
        //   setHighlightedTurnId(null);
        // }, 3000);
      }
    }
  }, [transcriptId, transcriptDetail, location.state?.highlightTurnId]);

  useEffect(() => {
    if (scrollToTurnId != null && transcriptDetail?.transcript && tempTranscripts.length > 0) {
      // First try: match ID + search term
      let matchingTurn = null;
      if (searchTerm) {
        matchingTurn = tempTranscripts.find(
          (turn) =>
            String(turn.id) === String(scrollToTurnId) &&
            turn.text &&
            turn.text.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }

      // Fallback: just match by ID
      if (!matchingTurn) {
        matchingTurn = tempTranscripts.find((turn) => String(turn.id) === String(scrollToTurnId));
      }

      if (matchingTurn) {
        console.log('Debug - Setting highlighted turn ID:', matchingTurn.id);
        setHighlightedTurnId(matchingTurn.id);

        // Scroll to the matching turn after a brief delay to ensure rendering is complete
        setTimeout(() => {
          const turnElement = autoScrollWhilePlayingRef.current[matchingTurn.id];
          console.log('Debug - Turn element found:', !!turnElement);
          if (turnElement) {
            turnElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 500);

        // Optional: clear the highlighting after a few seconds
        setTimeout(() => {
          setHighlightedTurnId(null);
        }, 4000);
      } else {
        console.log('Debug - No matching turn found for ID:', scrollToTurnId);
      }
    }
  }, [scrollToTurnId, searchTerm, tempTranscripts, transcriptDetail?.transcript]);

  useEffect(() => {
    // NOTE: Code filtering removed - codes are now stored only in analysis_item
    // Always show all transcripts without code-based filtering
    setTempTranscripts(transcriptDetail.transcript);
  }, [transcriptId, transcriptDetail]);

  const closeModal = () => {
    setOpenCodeModal(false);
    setSelectedCode(null);
  };

  const getTranscriptName = () => {
    if (transcriptDetail?.name) {
      return transcriptDetail.name;
    } else {
      let transcriptIndex = transcripts?.transcripts?.findIndex((item) => item.id === transcriptId);
      return `Transcript ${transcriptIndex + 1}`;
    }
  };

  const qc = useQueryClient();
  const handleEditTranscriptText = async () => {
    let payload = {
      study_id: id,
      qualitative_item_id: transcriptId,
      text: editedTranscriptText,
      turn_id: editingItem,
    };

    try {
      await editTranscriptText(payload);
      setEditingItem(null);
      setHighlightedItemId(null);
      setEditedTranscriptText('');
      qc.invalidateQueries({ queryKey: ['transcript'] });
    } catch (error) {
      console.error('Failed to add code. Please try again.', error?.detail || error);
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: editTranscriptName,
    },
  });

  const onSubmit = (data) => {
    updateTranscriptName(
      {
        payload: data,
        transcriptId,
        id,
      },
      {
        onSuccess: handleClosePopup3,
      },
    );
  };
  // NOTE: getCodesCount removed - codes are now stored only in analysis_item
  // This function is no longer needed as inline code tags are not displayed

  // Calculate turn statistics
  const calculateTurnStatistics = (transcript) => {
    if (!transcript || transcript.length === 0) {
      return {
        totalTurns: 0,
        aiTurns: 0,
        humanTurns: 0,
        averageWordsPerTurn: 0,
      };
    }

    const totalTurns = transcript.length;
    const aiTurns = transcript.filter(
      (turn) => getSpeakerRole(turn.speaker_id) === 'Interviewer',
    ).length;
    const humanTurns = totalTurns - aiTurns;

    const totalWords = transcript.reduce((sum, turn) => {
      const words = turn.text.split(/\s+/).filter((word) => word.length > 0);
      return sum + words.length;
    }, 0);
    const averageWordsPerTurn = totalTurns > 0 ? Math.round(totalWords / totalTurns) : 0;

    return {
      totalTurns,
      aiTurns,
      humanTurns,
      averageWordsPerTurn,
    };
  };

  // NOTE: codeList and filteredData removed - codes are now stored only in analysis_item
  // These variables are no longer needed as inline code tags are not displayed

  // Map lens types to their components
  const analysisComponents = {
    phenomenological: PhenomenologicalAnalysisTranscript,
    narrative_arc: NarrativeArcAnalysisTranscript,
    jtbd_map: JtbdMapAnalysisTranscript,
    journey_pain: JourneyPainAnalysisTranscript, // not found
    persona_task: PersonaTaskAnalysisTranscript,
    sentiment_spectrum: SentimentSpectrumAnalysisTranscript,
    framing_discourse: FramingDiscourseAnalysisTranscript,
    resonance_scan: ResonanceScanAnalysisTranscript,
    outcome_barrier: OutcomeBarrierAnalysisTranscript, // not found
    equity_audit: EquityAuditAnalysisTranscript,
    toc_alignment: TocAlignmentAnalysisTranscript,
    saturation_gauge: SaturationGaugeAnalysisTranscript,
    reflexivity_tracker: ReflexivityTrackerAnalysisTranscript,
    custom_lens: CustomAnalysisTranscript,
  };

  if (isPending) {
    return <Spinner />;
  }

  if (isEmpty(transcriptDetail) && !isPending) {
    return <Typography p={5}>Select a Transcript to view details</Typography>;
  }

  const open3 = editTranscriptName ? true : false;
  const pop3Id = open3 ? 'simple-popover' : undefined;

  const handleClosePopup3 = () => {
    setEditTranscriptName('');
  };

  const getSpeakerName = (speaker_id) => {
    let speaker = transcriptDetail?.speakers?.find((item) => item.id === speaker_id);
    return formatInTitleCase(speaker?.name ?? '') || '';
  };

  const getSpeakerRole = (speaker_id) => {
    let speaker = transcriptDetail?.speakers?.find((item) => item.id === speaker_id);
    return formatInTitleCase(speaker?.role ?? '') || '';
  };

  // Disable edit controls based on permissions
  const showEditControls = canEdit;

  const semanticStatus = (
    transcriptDetail?.semantic_embedding_status || 'NOT_STARTED'
  ).toUpperCase();
  const semanticError = transcriptDetail?.semantic_embedding_error || '';
  const isQuotaExceeded =
    semanticError.includes('SEARCH_QUOTA_EXCEEDED') ||
    semanticError.includes('Storage quota has been exceeded');
  const quotaMessage = 'Semantic search is temporarily unavailable. Please contact support.';
  const semanticMeta = (() => {
    if (semanticStatus === 'COMPLETED') {
      return {
        label: 'Semantic search ready',
        color: 'success',
        icon: <CheckCircleOutlineIcon fontSize="small" color="success" />,
        clickable: false,
      };
    }
    if (semanticStatus === 'IN_PROGRESS') {
      return {
        label: 'Preparing semantic search. This usually takes a few minutes.',
        color: 'info',
        icon: <HourglassTopIcon fontSize="small" color="info" />,
        clickable: false,
      };
    }
    if (semanticStatus === 'FAILED') {
      return {
        label: isQuotaExceeded ? quotaMessage : 'Semantic search failed. Click to retry.',
        color: 'error',
        icon: <ErrorOutlineIcon fontSize="small" color="error" />,
        clickable: !isQuotaExceeded,
      };
    }
    return {
      label:
        'Prepare semantic search to enable meaning‑based matches. This may take a few minutes.',
      color: 'default',
      icon: <ManageSearchIcon fontSize="small" color="action" />,
      clickable: true,
    };
  })();

  const semanticChipLabel =
    semanticStatus === 'COMPLETED'
      ? 'Semantic Ready'
      : semanticStatus === 'IN_PROGRESS'
        ? 'Semantic Indexing'
        : semanticStatus === 'FAILED'
          ? isQuotaExceeded
            ? 'Semantic Unavailable'
            : 'Retry Semantic'
          : 'Prepare Semantic';

  const handleQueueSemanticEmbedding = () => {
    if (!transcriptId) return;
    queueSemanticEmbedding({ studyId: id, transcriptIds: [transcriptId] });
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: participantDetailsExpanded ? '1.2fr 0.8fr' : '1fr auto',
          },
          gap: 2,
          mb: 2,
          transition: 'grid-template-columns 0.3s ease',
        }}
      >
        <Card
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: 'divider',
            maxWidth: participantDetailsExpanded ? 'none' : '800px',
            mx: participantDetailsExpanded ? 0 : 'auto',
            transition: 'max-width 0.3s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <VideocamOutlinedIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Recording
            </Typography>
          </Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: '#000',
            }}
          >
            <RecordingFile
              ref={playerRef}
              playing={playing}
              setPlaying={setPlaying}
              mediaData={transcriptDetail}
              isLoadingMedia={isPending}
              playingTurnItem={playingTurnItem}
              setPlayedSeconds={setPlayedSeconds}
              transcriptId={transcriptId}
              useAspectRatio
              aspectRatio="16/9"
            />
          </Box>
        </Card>

        <ParticipantDetailsCollapsible
          participantData={participant?.data[0]}
          defaultExpanded
          onExpandChange={setParticipantDetailsExpanded}
        />
      </Box>

      <Card sx={{ padding: 2, borderRadius: 1 }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: '5px', marginBottom: '10px', alignItems: 'center' }}>
            <TextSnippetOutlinedIcon />{' '}
            {!canEdit && (
              <Chip
                sx={{ ml: 2 }}
                label="View Only"
                color="warning"
                variant="outlined"
                size="small"
              />
            )}
            {showEditControls && (
              <Typography
                sx={{
                  fontWeight: '600',
                  cursor: 'pointer',
                  px: '4px',
                  backgroundColor: editTranscriptName ? '#e8e8e8' : '',
                  borderRadius: '4px',
                  ':hover': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
                onClick={(e) => {
                  handleOpenPopoverTranscriptName(e);
                  reset({ name: getTranscriptName() });
                  setEditTranscriptName(getTranscriptName());
                }}
              >
                {transcriptName}
              </Typography>
            )}
            <Tooltip title={semanticMeta.label} arrow>
              <span>
                <Chip
                  icon={
                    semanticStatus === 'IN_PROGRESS' && isQueueingSemanticEmbedding ? (
                      <CircularProgress size={14} />
                    ) : (
                      semanticMeta.icon
                    )
                  }
                  label={semanticChipLabel}
                  variant="outlined"
                  size="small"
                  clickable={semanticMeta.clickable}
                  onClick={semanticMeta.clickable ? handleQueueSemanticEmbedding : undefined}
                  disabled={!semanticMeta.clickable || isQueueingSemanticEmbedding}
                  sx={{ ml: 1 }}
                />
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Turn Statistics */}
        {transcriptDetail?.transcript && transcriptDetail.transcript.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: 2,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              marginTop: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {(() => {
              const stats = calculateTurnStatistics(transcriptDetail.transcript);
              return (
                <>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {stats.totalTurns}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Turns
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {stats.aiTurns}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AI Turns
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {stats.humanTurns}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Human Turns
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {stats.averageWordsPerTurn}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Words/Turn
                    </Typography>
                  </Box>
                </>
              );
            })()}
          </Box>
        )}

        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList
              onChange={handleChange}
              aria-label="lab API tabs example"
              sx={{ textTransform: 'none', marginTop: '20px' }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab sx={{ textTransform: 'none' }} label="Transcript" value="1" />
              <Tab sx={{ textTransform: 'none' }} label="Summary" value="2" />
              {/* Dynamically add tabs for each lens summary */}
              {!isLoadingLensSummaries &&
                lensSummaries?.lens_summaries &&
                lensSummaries.lens_summaries.length > 0 &&
                lensSummaries.lens_summaries.map((lens, index) => (
                  <Tab
                    key={lens.analysis_lens_type || index}
                    sx={{ textTransform: 'none' }}
                    label={formatLensType(lens.analysis_lens_type)}
                    value={`lens-${lens.analysis_lens_type}`} // Unique value
                  />
                ))}
            </TabList>
          </Box>

          <TabPanel value="1" sx={{ padding: '0' }}>
            <>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                marginBottom={'20px'}
                marginTop={'20px'}
              >
                {/* NOTE: TranscriptCodeFilter removed - code filtering is no longer needed
                    since codes are now stored only in analysis_item, not on transcripts.
                */}
              </Box>
              <Card variant="outlined" sx={{ p: 0, pb: 2, width: '100%', marginTop: '20px' }}>
                <Box
                  display={'flex'}
                  px={2}
                  py={1.3}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Typography variant="subtitle2">Tags</Typography>
                </Box>
                <Divider />
                <Box
                  display={'flex'}
                  gap={2}
                  alignItems={'center'}
                  p={2}
                  justifyContent={'space-between'}
                >
                  {transcriptDetail?.assigned_tags?.length <= 0 ? (
                    <Box>Empty assigned tags</Box>
                  ) : (
                    <Box width={'calc(100% - 200px)'} mt={1.5}>
                      <AssignedTagInTranscript
                        transcriptDetail={transcriptDetail}
                        canEdit={canEdit}
                      />
                    </Box>
                  )}

                  <Tooltip title={canEdit ? '' : 'You do not have permission to assign tag'}>
                    <Button
                      sx={{
                        cursor: canEdit ? 'pointer' : 'not-allowed',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                      variant="contained"
                      onClick={() => setOpenTagModal(true)}
                      disabled={!canEdit}
                    >
                      {' '}
                      <Icon icon="streamline-plump:tag-alt-remix" width="20" height="20" /> Assign
                      Tag
                    </Button>
                  </Tooltip>
                </Box>
              </Card>

              <Card variant="outlined" sx={{ p: 0, pb: 2, width: '100%', marginTop: '20px' }}>
                <Box
                  display={'flex'}
                  px={2}
                  py={1.3}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Typography variant="subtitle2">Transcript</Typography>
                </Box>
                <Divider />
                <Box ref={transcriptBlcokRef} sx={{ position: 'relative' }}>
                  {tempTranscripts?.map((item, index) => {
                    // const highlightColor = hoveredCode
                    //   ? hashStringToColor(hoveredCode)
                    //   : 'transparent';

                    const startSec = item.start_time / 1000;
                    const endSec = item.end_time / 1000;

                    const isActive = playedSeconds >= startSec && playedSeconds <= endSec;

                    return (
                      <React.Fragment key={item.id}>
                        <Box
                          key={index}
                          p={1}
                          className={`box-base ${isActive && playing ? 'activeturn' : ''}`}
                          sx={{
                            borderRadius: 1,
                            backgroundColor: 'transparent',
                            border: '2px solid transparent',
                            transition: 'all 0.3s ease-in-out',
                          }}
                          position={'relative'}
                          onMouseEnter={() => setHoveredBoxId(item.id)}
                          onMouseLeave={() => setHoveredBoxId(null)}
                        >
                          {showEditControls && hoveredBoxId === item.id && !editingItem && (
                            <IconButton
                              variant="custom"
                              sx={{
                                position: 'absolute',
                                backgroundColor: '#d9e8fe',
                                ':hover': {
                                  backgroundColor: '#d9e8fe',
                                },
                                top: '4px',
                                right: '4px',
                                zIndex: '100',
                              }}
                              onMouseEnter={() => setHighlightedItemId(item.id)}
                              onMouseLeave={() => setHighlightedItemId(null)}
                              onClick={() => {
                                setEditingItem(item.id);
                                setEditedTranscriptText(item.text);
                              }}
                            >
                              <Icon icon="line-md:edit-filled" width="24" height="24" />
                            </IconButton>
                          )}

                          <Box>
                            <Box
                              display={'flex'}
                              gap={'10px'}
                              color={'#3A2A6B'}
                              fontWeight={'bold'}
                              my={1}
                              ref={(el) => {
                                autoScrollWhilePlayingRef.current[item.id] = el;
                              }}
                            >
                              {getSpeakerRole(item?.speaker_id) === 'Interviewer' ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '32px',
                                    width: '32px',
                                    backgroundColor: '#d9e8fe',
                                    borderRadius: '24px',
                                    cursor: canEdit ? 'pointer' : 'default',
                                  }}
                                  onClick={(e) => {
                                    if (!canEdit) return;
                                    setSelectedItem(item);
                                    handleOpenInterviewPopover(e);
                                  }}
                                >
                                  {/* {getShortSpeakerName(item?.speaker_id)} */}
                                  <UserMic />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '32px',
                                    width: '32px',
                                    cursor: canEdit ? 'pointer' : 'default',
                                    backgroundColor: '#7d5ae8',
                                    borderRadius: '24px',
                                  }}
                                  onClick={(e) => {
                                    if (!canEdit) return;
                                    setSelectedItem(item);
                                    handleOpenInterviewPopover(e);
                                  }}
                                >
                                  {/* {getShortSpeakerName(item?.speaker_id)} */}
                                  <Person2OutlinedIcon sx={{ color: '#fff' }} />
                                </Box>
                              )}

                              <Box display={'flex'} flexDirection={'column'}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                  {getSpeakerName(item?.speaker_id)}
                                </Typography>
                                <Typography
                                  sx={{
                                    backgroundColor:
                                      openPopoverInterview && selectedItem.id === item.id
                                        ? `${getSpeakerRole(item?.speaker_id) === 'Interviewer' ? '#d9e8fe' : '#f6e7fa'}`
                                        : '',
                                    // px: '4px',
                                    borderRadius: '4px',
                                    cursor: canEdit ? 'pointer' : 'default',
                                    lineHeight: '18px',
                                  }}
                                  onClick={(e) => {
                                    if (!canEdit) return;
                                    setSelectedItem(item);
                                    handleOpenInterviewPopover(e);
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: '12px',
                                      backgroundColor:
                                        getSpeakerRole(item?.speaker_id) === 'Interviewer'
                                          ? '#d9e8fe'
                                          : '#f6e7fa',
                                      padding: '2px 4px',

                                      borderRadius: '3px',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    Role: {getSpeakerRole(item?.speaker_id)}
                                  </span>
                                </Typography>
                              </Box>

                              {transcriptDetail?.blob_storage_url &&
                                transcriptDetail.media_type !== 'text' && (
                                  <Box display={'flex'} alignItems={'center'} gap={1}>
                                    {playing && playingTurnItem?.id === item?.id ? (
                                      <Typography
                                        height={'30px'}
                                        width={'30px'}
                                        onClick={() => {
                                          setPlaying(false);
                                          setPlayingTurnItem(null);
                                          if (transcriptDetail.media_type === mediaType.audio) {
                                            const audio = playerRef.current;
                                            audio.pause();
                                          }
                                        }}
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          ':hover': {
                                            backgroundColor: '#d9d9d9',
                                          },
                                        }}
                                      >
                                        <Icon icon="gg:play-pause" width="20" height="20" />
                                      </Typography>
                                    ) : (
                                      <Typography
                                        height={'30px'}
                                        width={'30px'}
                                        onClick={() => {
                                          setPlaying(true);
                                          setPlayingTurnItem(item);
                                          handleSeek(item);
                                        }}
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          ':hover': {
                                            backgroundColor: '#d9d9d9',
                                          },
                                        }}
                                      >
                                        <Icon
                                          icon="ic:sharp-play-arrow"
                                          width="19"
                                          color="#8c8c8c"
                                          height="19"
                                        />
                                      </Typography>
                                    )}

                                    <Typography color={'#8c8c8c'} ml={-0.8}>
                                      {formatMsToMin(item?.start_time)}
                                    </Typography>
                                  </Box>
                                )}
                            </Box>

                            {/* here */}
                            <Box flex={1}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  flex: 1,
                                  gap: 1,
                                }}
                              >
                                {editingItem === item.id ? (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      marginTop: '5px',
                                      marginBottom: '20px',
                                    }}
                                  >
                                    <TextareaAutosize
                                      ref={textareaRef}
                                      minRows={editedTranscriptText?.length > 200 ? 6 : 4}
                                      value={editedTranscriptText}
                                      onChange={(e) => setEditedTranscriptText(e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #ccc',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        boxSizing: 'border-box',
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'end',
                                        gap: 1,
                                        mt: 0.5,
                                      }}
                                    >
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                          setEditingItem(null);
                                          setHighlightedItemId(null);
                                          setEditedTranscriptText('');
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleEditTranscriptText}
                                      >
                                        {'Save'}
                                      </Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    mt={0.1}
                                    mb={1}
                                    sx={{
                                      position: 'relative',
                                      backgroundColor:
                                        highlightedItemId === item.id ? '#f4bebe' : 'transparent',
                                      transition: 'background-color 0.2s ease',
                                    }}
                                  >
                                    {getSpeakerRole(item?.speaker_id) === 'Interviewer' ? (
                                      <Typography
                                        className={`${isActive && playing ? 'highlight-active-text ' : ''}`}
                                      >
                                        {highlightSearchTerm(
                                          item?.text,
                                          searchTerm,
                                          item.id,
                                          highlightedTurnId,
                                        )}
                                      </Typography>
                                    ) : (
                                      <Box>
                                        {highlightedTurnId === item.id && searchTerm ? (
                                          <Typography
                                            className={`${isActive && playing ? 'highlight-active-text ' : ''}`}
                                          >
                                            {highlightSearchTerm(
                                              item?.text,
                                              searchTerm,
                                              item.id,
                                              highlightedTurnId,
                                            )}
                                          </Typography>
                                        ) : (
                                          <>
                                            {item?.text}
                                            {/* code highlight and assign code is disabled for now */}

                                            {/* <AssignCodeInTranscript
                                              canEdit={canEdit}
                                              containerRef={transcriptBlcokRef}
                                              text={item?.text}
                                              highlightColor={highlightColor}
                                              index={item?.id}
                                              item={item}
                                              study_id={id}
                                              interview={interview}
                                              transcriptId={transcriptId}
                                              codesQuotesDict={codesQuotesDict}
                                              setCodesQuotesDict={setCodesQuotesDict}
                                              hoveredCode={hoveredCode}
                                              getSpeakerRole={getSpeakerRole}
                                              getColorByCodeName={getColorByCodeName}
                                              isActive={isActive}
                                              playing={playing}
                                            /> */}
                                          </>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                              {/* NOTE: Inline code tags removed - codes are now stored only in analysis_item.
                                  Thematic analysis results can be viewed in the Analysis view.
                              */}
                            </Box>
                          </Box>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Card>
            </>
          </TabPanel>
          <TabPanel value="2" sx={{ padding: '0' }}>
            {isLoadingSummary ? (
              <Box
                p={4}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                <Spinner />
                <Typography variant="body2" color="text.secondary">
                  Generating summary... This may take a few seconds.
                </Typography>
              </Box>
            ) : transcriptSummary ? (
              <>
                <Card variant="outlined" sx={{ p: 0, pb: 2, width: '100%', marginTop: '20px' }}>
                  <Box
                    display={'flex'}
                    px={2}
                    py={1.3}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Typography variant="subtitle2">{transcriptName} Summary</Typography>
                  </Box>
                  <Divider />
                  <Typography p={2}>{transcriptSummary?.summary?.summary}</Typography>
                </Card>
                <Card variant="outlined" sx={{ p: 0, pb: 2, width: '100%', marginTop: '20px' }}>
                  <Box
                    display={'flex'}
                    px={2}
                    py={1.3}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Typography variant="subtitle2" display={'flex'} alignItems={'center'} gap={1}>
                      <Icon icon="fluent:key-multiple-24-regular" width="24" height="24" /> Key
                      Takeaways
                    </Typography>
                  </Box>
                  <Divider />
                  <Box p={2} mt={-2}>
                    {transcriptSummary?.summary?.answers?.length > 0 && (
                      <Box>
                        {transcriptSummary?.summary?.answers.map((item, index) => (
                          <Box key={index} mt={2}>
                            <Typography variant="subtitle2">
                              {index + 1}. {item?.question}
                            </Typography>
                            <Typography my={0.5}>{item?.insight}</Typography>

                            {item?.quotes?.length > 0 && (
                              <Box>
                                <Typography my={1} mb={0.6}>
                                  <strong>Supporting Quotes</strong>
                                </Typography>
                                {item.quotes.map((quote, quoteIndex) => (
                                  <Box
                                    key={quoteIndex}
                                    sx={{ marginBottom: '0.5em', paddingLeft: '1em' }}
                                  >
                                    <Typography variant="body2" fontStyle={'italic'}>
                                      {quote}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Card>
              </>
            ) : (
              <Box p={2} m={2} display={'flex'} justifyContent={'center'}>
                Not available now
              </Box>
            )}
          </TabPanel>

          {/* Dynamically add panels for each lens summary */}
          {!isLoadingLensSummaries &&
            lensSummaries?.lens_summaries &&
            lensSummaries.lens_summaries.length > 0 &&
            lensSummaries.lens_summaries.map((lens, index) => {
              const LensComponent = analysisComponents[lens.analysis_lens_type];
              return LensComponent ? (
                <TabPanel
                  key={lens.analysis_lens_type || index}
                  value={`lens-${lens.analysis_lens_type}`} // Match tab value
                  sx={{ padding: '0' }}
                >
                  <LensComponent summaryData={lens} />
                </TabPanel>
              ) : null;
            })}
        </TabContext>
      </Card>

      {/* Modal for Code Details */}

      {openCodeModal && (
        <CodeQuotesModal
          transcripts={transcripts}
          open={openCodeModal}
          closeModal={closeModal}
          studyId={id}
          defaultTranscript={transcriptDetail}
          openCode={selectedCode}
          setOldCodesQuotesDict={() => {}}
          setSelectedCode={setSelectedCode}
          queryKey="transcript"
          canEdit={canEdit}
        />
      )}

      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title="Unassign Code"
        isPending={false}
        content={`This will unassign the code from the selected section of the conversation. The code will remain in other sections where it is assigned. You can reassign the code to this section later if needed.`}
        okText={'Unassign'}
      />
      <EditInterviewer
        openPopoverInterview={openPopoverInterview}
        setOpenPopoverInterview={setOpenPopoverInterview}
        selectedItem={selectedItem}
        speakers={transcriptDetail?.speakers}
        transcriptId={transcriptId}
        canEdit={canEdit}
        id={id}
      />
      <AssignTags
        id={id}
        onClose={handleCloseTagModal}
        open={openTagModal}
        tags={tagsList}
        searchQuery={tagSearchQuery}
        setSearchQuery={setTagSearchQuery}
        transcriptId={transcriptId}
        isFetchingTags={isFetchingTags}
        addStudyTag={addStudyTag}
        isAddingStudyTag={isAddingStudyTag}
        assigned_tags={transcriptDetail?.assigned_tags}
        canEdit={canEdit}
      />

      <Popover
        id={pop3Id}
        open={editTranscriptName ? true : false}
        anchorEl={openPopoverTranscriptName}
        onClose={handleClosePopup3}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box p={2}>
            <Typography>ID : {transcriptId}</Typography>
            <TextField
              inputRef={editTranscriptNameRef}
              {...register('name', { required: 'Transcript name is required' })}
              placeholder={'Transcript 1'}
              variant="outlined"
              size="small"
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={{
                mt: 1,
              }}
              fullWidth
            />
            <Box mt={1.5} display={'flex'}>
              <Button
                sx={{
                  ml: 'auto',
                  cursor: 'pointer',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                type="submit"
                disabled={isUpdatingTranscriptName}
                variant="contained"
              >
                {isUpdatingTranscriptName ? 'Saving' : 'Save'}
              </Button>
            </Box>
          </Box>
        </form>
      </Popover>
    </>
  );
};

export default TranscriptDetail;
